import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, User, X, MessageCircle, Briefcase } from "lucide-react";
import logoWebp from "@/assets/logo.webp";
import logoPng from "@/assets/logo.png";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n/LanguageContext";
import { useCustomerAuth } from "@/auth/CustomerAuthContext";
import { trackCtaClick } from "@/lib/analytics";
import { whatsappLinkProps } from "@/lib/whatsapp";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

/**
 * Solutions group — surfaced via a desktop dropdown and a mobile
 * collapsible. Each item has a short descriptor shown in the dropdown.
 */
const SOLUTIONS_NAV = [
  {
    to: "/fast-access-power-components",
    key: "nav.fastAccess",
    descKey: "nav.fastAccess.desc",
  },
  {
    to: "/pre-configured-power-packages",
    key: "nav.kits",
    descKey: "nav.kits.desc",
  },
  {
    to: "/power-systems",
    key: "nav.engineered",
    descKey: "nav.engineered.desc",
  },
] as const;

// Top-level items (What We Source, How It Works) are rendered inline
// alongside the Solutions dropdown — no shared array needed.

const Header = () => {
  const { pathname } = useLocation();
  const { t, lang, localePath } = useLang();
  const { user, signOut } = useCustomerAuth();
  const [hideAtBottom, setHideAtBottom] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const accountHref = localePath(user ? "/account" : "/account?mode=signin");
  const accountLabel = user
    ? { en: "My account", es: "Mi cuenta", pt: "Minha conta" }[lang]
    : { en: "Sign in", es: "Iniciar sesión", pt: "Entrar" }[lang];
  const mobileAccountLabel = user
    ? { en: "My account", es: "Mi cuenta", pt: "Minha conta" }[lang]
    : {
        en: "Sign in / Create account",
        es: "Iniciar sesión / Crear cuenta",
        pt: "Entrar / Criar conta",
      }[lang];

  const handleLogoClick = () => {
    if (pathname === "/" || pathname === "/es" || pathname === "/pt") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.dispatchEvent(new Event("home:reset"));
    }
  };

  useEffect(() => {
    // Desktop-only behavior: hide the sticky header when the footer enters
    // the viewport. Skip the entire scroll/resize wiring on mobile to avoid
    // unnecessary listeners + layout reads on the LCP-critical viewport.
    const mql = window.matchMedia("(min-width: 768px)");
    if (!mql.matches) {
      // Make sure the state is reset if we crossed from desktop → mobile.
      setHideAtBottom(false);
      return;
    }

    let ticking = false;
    let cachedFooter: HTMLElement | null = null;

    const update = () => {
      ticking = false;
      // Cache the footer element so we don't pay the querySelector cost on
      // every scroll frame. Re-query if the cached node was unmounted.
      if (!cachedFooter || !cachedFooter.isConnected) {
        cachedFooter = document.querySelector("footer");
      }
      if (!cachedFooter) return;
      const rect = cachedFooter.getBoundingClientRect();
      setHideAtBottom(rect.top < window.innerHeight);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    // Defer the first measurement to rAF so we don't synchronously read
    // layout during mount (avoids the forced-reflow Lighthouse flag).
    requestAnimationFrame(update);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathname]);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-border bg-background transition-transform duration-300 ${
        hideAtBottom ? "md:-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="container flex h-14 items-center justify-between gap-2 px-4 sm:h-16 md:h-24">
        <Link
          to={localePath("/")}
          onClick={handleLogoClick}
          aria-label="Switzh home"
          className="flex shrink-0 items-center"
        >
          <picture>
            <source srcSet={logoWebp} type="image/webp" />
            <img
              src={logoPng}
              alt="Switzh — U.S. Electrical Sourcing. Delivered Across LATAM."
              className="h-10 w-auto sm:h-11 md:h-14"
              width={1397}
              height={318}
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </Link>

        <nav className="flex items-center gap-1.5 sm:gap-2 md:gap-7 lg:gap-9">
          {/* Desktop nav — What We Source */}
          <Link
            to={localePath("/what-we-source")}
            className="group relative hidden text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-primary md:inline-block"
          >
            <span>{t("nav.whatWeSource")}</span>
            <span aria-hidden="true" className="absolute -bottom-1 left-0 h-0.5 w-full origin-left scale-x-0 bg-primary transition-transform duration-200 group-hover:scale-x-100" />
          </Link>

          {/* Desktop Solutions dropdown */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto bg-transparent px-0 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-transparent hover:text-primary focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-primary">
                  {t("nav.solutions")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-1 p-3">
                    {SOLUTIONS_NAV.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={localePath(item.to)}
                          className="block rounded-md p-3 transition-colors hover:bg-secondary focus:bg-secondary focus:outline-none"
                        >
                          <div className="text-sm font-semibold text-foreground">
                            {t(item.key)}
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {t(item.descKey)}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Desktop nav — How It Works */}
          <Link
            to={localePath("/how-it-works")}
            className="group relative hidden text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-primary md:inline-block"
          >
            <span>{t("nav.howItWorks")}</span>
            <span aria-hidden="true" className="absolute -bottom-1 left-0 h-0.5 w-full origin-left scale-x-0 bg-primary transition-transform duration-200 group-hover:scale-x-100" />
          </Link>

          {/* Language switcher — desktop only; on mobile it lives in the menu */}
          <div className="hidden md:ml-2 md:block">
            <LanguageSwitcher showFlagInTrigger={false} />
          </div>

          {/* Desktop account: Sign in/out (text) + account/create CTA */}
          {!user ? (
            <div className="hidden items-center gap-5 md:flex md:ml-2">
              <Link
                to={localePath("/account?mode=signin")}
                className="text-sm font-normal leading-none text-muted-foreground transition-colors duration-150 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
              >
                {{ en: "Sign in", es: "Iniciar sesión", pt: "Entrar" }[lang]}
              </Link>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-10 border-border/70 bg-transparent px-4 text-sm font-medium text-foreground/80 transition-all duration-150 hover:border-primary/40 hover:bg-secondary hover:text-foreground"
              >
                <Link to={localePath("/account?mode=signup")}>
                  {{ en: "Create account", es: "Crear cuenta", pt: "Criar conta" }[lang]}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="hidden items-center gap-5 md:flex md:ml-2">
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className="text-sm font-normal leading-none text-muted-foreground transition-colors duration-150 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
              >
                {{ en: "Sign out", es: "Cerrar sesión", pt: "Sair" }[lang]}
              </button>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-10 border-border/70 bg-transparent px-4 text-sm font-medium text-foreground/80 transition-all duration-150 hover:border-primary/40 hover:bg-secondary hover:text-foreground"
              >
                <Link to={accountHref}>
                  <User className="mr-2 h-4 w-4" />
                  {accountLabel}
                </Link>
              </Button>
            </div>
          )}

          <Button
            asChild
            size="sm"
            className="h-9 px-3 text-xs font-semibold shadow-sm transition-all duration-150 hover:shadow-md md:ml-2 sm:h-10 sm:px-4 sm:text-sm md:h-10 md:px-5"
          >
            <Link to={localePath("/request-quote")} onClick={() => trackCtaClick("header", "request_quote")}>
              {t("nav.getQuote")}
            </Link>
          </Button>

          {/* Mobile menu trigger — hidden on md+ */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                className="h-11 w-11 md:hidden [&_svg]:!size-7"
              >
                <Menu className="h-7 w-7" strokeWidth={1.75} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 border-0 p-0 sm:max-w-none [&>button]:hidden"
            >
              <SheetTitle className="sr-only">{t("nav.solutions")}</SheetTitle>

              {/* Top bar: logo + prominent close */}
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <Link
                  to={localePath("/")}
                  onClick={() => setMobileOpen(false)}
                  aria-label="Switzh home"
                  className="flex items-center"
                >
                  <picture>
                    <source srcSet={logoWebp} type="image/webp" />
                    <img
                      src={logoPng}
                      alt="Switzh"
                      className="h-9 w-auto"
                      width={1397}
                      height={318}
                      decoding="async"
                    />
                  </picture>
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-secondary text-foreground transition-colors hover:bg-foreground hover:text-background"
                >
                  <X className="h-6 w-6" strokeWidth={2.25} />
                </button>
              </div>

              {/* Scrollable nav body */}
              <nav className="flex-1 overflow-y-auto px-4 py-4">
                <Link
                  to={localePath("/what-we-source")}
                  className="block rounded-md px-2 py-3 text-lg font-semibold text-foreground transition-colors hover:bg-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("nav.whatWeSource")}
                </Link>

                <div className="mt-2 px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("nav.solutions")}
                </div>
                <div className="flex flex-col gap-0.5">
                  {SOLUTIONS_NAV.map((item) => (
                    <Link
                      key={item.to}
                      to={localePath(item.to)}
                      className="rounded-md px-2 py-2.5 transition-colors hover:bg-secondary"
                      onClick={() => setMobileOpen(false)}
                    >
                      <div className="text-base font-semibold text-foreground">
                        {t(item.key)}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {t(item.descKey)}
                      </p>
                    </Link>
                  ))}
                </div>

                <div className="my-3 h-px bg-border" />

                <Link
                  to={localePath("/how-it-works")}
                  className="block rounded-md px-2 py-3 text-lg font-semibold text-foreground transition-colors hover:bg-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("nav.howItWorks")}
                </Link>

                {/* Language — better positioned, easier to tap */}
                <div className="mt-4 flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {{ en: "Language", es: "Idioma", pt: "Idioma" }[lang]}
                  </span>
                  <LanguageSwitcher variant="full" align="end" />
                </div>
              </nav>

              {/* Bottom CTA stack */}
              <div className="border-t border-border bg-background px-4 py-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                {/* Account: small "Sign in" above, Create account CTA */}
                {!user && (
                  <div className="mb-3">
                    <Link
                      to={localePath("/account?mode=signin")}
                      onClick={() => setMobileOpen(false)}
                      className="mb-1.5 block text-center text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      {{ en: "Already have an account? Sign in", es: "¿Ya tienes cuenta? Inicia sesión", pt: "Já tem conta? Entrar" }[lang]}
                    </Link>
                    <Button
                      asChild
                      variant="outline"
                      className="h-11 w-full text-sm font-semibold"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link to={localePath("/account?mode=signup")}>
                        <User className="mr-2 h-4 w-4" />
                        {{ en: "Create account", es: "Crear cuenta", pt: "Criar conta" }[lang]}
                      </Link>
                    </Button>
                  </div>
                )}
                {user && (
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileOpen(false);
                        void signOut();
                      }}
                      className="mb-1.5 block w-full text-center text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      {{ en: "Sign out", es: "Cerrar sesión", pt: "Sair" }[lang]}
                    </button>
                    <Button
                      asChild
                      variant="outline"
                      className="h-11 w-full text-sm font-semibold"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link to={accountHref}>
                        <User className="mr-2 h-4 w-4" />
                        {mobileAccountLabel}
                      </Link>
                    </Button>
                  </div>
                )}

                {/* Supplier login CTA */}
                <Button
                  asChild
                  variant="outline"
                  className="mb-3 h-11 w-full text-sm font-semibold"
                  onClick={() => setMobileOpen(false)}
                >
                  <Link to={localePath("/supplier-login")}>
                    <Briefcase className="mr-2 h-4 w-4" />
                    {{ en: "Supplier login", es: "Acceso proveedores", pt: "Acesso fornecedores" }[lang]}
                  </Link>
                </Button>

                {/* WhatsApp CTA */}
                <Button
                  asChild
                  variant="whatsapp"
                  className="mb-3 h-12 w-full text-sm font-semibold"
                >
                  <a
                    {...whatsappLinkProps("header_mobile_menu")}
                    onClick={() => setMobileOpen(false)}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" strokeWidth={2.25} />
                    {t("cta.chatWhatsapp")}
                  </a>
                </Button>

                {/* Primary CTA */}
                <Button
                  asChild
                  className="h-12 w-full text-base font-semibold"
                  onClick={() => {
                    trackCtaClick("header_mobile", "request_quote");
                    setMobileOpen(false);
                  }}
                >
                  <Link to={localePath("/request-quote")}>{t("nav.getQuote")}</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
